import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useAuth } from '../hooks/useAuth';
import { PageHeader } from '../components/layout/AppShell';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';
import { connect, credits as creditsApi } from '../lib/api';
import { track } from '../lib/analytics';
import { apiErrorMessage } from '../lib/errors';
import type { ConnectStatus, CreditPack, CreditsResponse } from '../lib/types';

/**
 * D-010 credit packs, used as display fallback until GET /v1/me/credits
 * returns its own packs[] (the API list always wins when present).
 */
const DEFAULT_PACKS: CreditPack[] = [
  { pack: '25', credits: 25, price_cents: 499, currency: 'USD' },
  { pack: '60', credits: 60, price_cents: 999, currency: 'USD' },
  { pack: '150', credits: 150, price_cents: 1999, currency: 'USD' },
];

export function Account() {
  const { t, fmt, locale, setLocale } = useI18n();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();

  // ── Get-paid card state ────────────────────────────────────────────────────
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null>(null);
  const [connectLoading, setConnectLoading] = useState(true);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  // ── Credits card state ─────────────────────────────────────────────────────
  const [creditsData, setCreditsData] = useState<CreditsResponse | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [creditsError, setCreditsError] = useState<string | null>(null);
  const [buyingPack, setBuyingPack] = useState<string | null>(null);

  // Stripe credit-pack redirect lands on /account?credits=success|cancel.
  useEffect(() => {
    const outcome = params.get('credits');
    if (outcome === 'success') toast.push(t.credits.success, 'success');
    else if (outcome === 'cancel') toast.push(t.credits.cancel, 'info');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    connect
      .status(controller.signal)
      .then((s) => setConnectStatus(s))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setConnectError(apiErrorMessage(err, t));
      })
      .finally(() => setConnectLoading(false));
    creditsApi
      .get(controller.signal)
      .then((c) => setCreditsData(c))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setCreditsError(apiErrorMessage(err, t));
      })
      .finally(() => setCreditsLoading(false));
    return () => controller.abort();
  }, [t]);

  async function startOnboarding() {
    setRedirecting(true);
    try {
      const res = await connect.onboard();
      window.location.href = res.onboarding_url;
    } catch (err) {
      toast.push(apiErrorMessage(err, t), 'error');
      setRedirecting(false);
    }
  }

  async function openDashboard() {
    setRedirecting(true);
    try {
      const res = await connect.dashboard();
      window.location.href = res.url;
    } catch (err) {
      toast.push(apiErrorMessage(err, t), 'error');
      setRedirecting(false);
    }
  }

  async function buyPack(pack: CreditPack) {
    setBuyingPack(pack.pack);
    track('checkout_started', {
      kind: 'credit_pack',
      pack: pack.pack,
      credits: pack.credits,
      price_cents: pack.price_cents,
      price_currency: pack.currency,
    });
    try {
      const res = await creditsApi.checkout(pack.pack);
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.push(apiErrorMessage(err, t), 'error');
      setBuyingPack(null);
    }
  }

  const state = connectStatus?.state ?? 'not_started';
  const payoutStateLabel = {
    not_started: t.payouts.stateNotStarted,
    in_progress: t.payouts.stateInProgress,
    restricted: t.payouts.stateRestricted,
    active: t.payouts.stateActive,
  }[state];
  const payoutBody = {
    not_started: t.payouts.notStarted,
    in_progress: t.payouts.inProgress,
    restricted: t.payouts.restricted,
    active: t.payouts.active,
  }[state];
  const payoutCta = state === 'not_started' ? t.payouts.setupCta : t.payouts.resumeCta;

  const packs = creditsData && creditsData.packs.length > 0 ? creditsData.packs : DEFAULT_PACKS;

  return (
    <div>
      <PageHeader title={t.account.title} />

      <div className="flex max-w-xl flex-col gap-4">
        {/* Get-paid card (Stripe Connect Express) */}
        <Card className="p-5">
          <div className="flex items-baseline justify-between gap-2">
            <p className="type-label text-secondary">{t.payouts.title}</p>
            {!connectLoading && !connectError ? (
              <span className={`type-label tnum ${state === 'active' ? 'text-success' : state === 'restricted' ? 'text-warn' : 'text-secondary'}`}>
                {payoutStateLabel}
              </span>
            ) : null}
          </div>
          {connectLoading ? (
            <div className="mt-3">
              <Skeleton className="h-5 w-2/3" />
              <span className="sr-only">{t.payouts.checking}</span>
            </div>
          ) : connectError ? (
            <p className="type-body-sm text-danger mt-3" role="alert">
              {connectError}
            </p>
          ) : (
            <>
              <p className="type-body text-primary mt-3">{payoutBody}</p>
              <div className="mt-4">
                {state === 'active' ? (
                  <Button variant="secondary" size="md" loading={redirecting} onClick={openDashboard}>
                    {redirecting ? t.payouts.opening : t.payouts.dashboardCta}
                  </Button>
                ) : (
                  <Button size="md" loading={redirecting} onClick={startOnboarding}>
                    {redirecting ? t.payouts.opening : payoutCta}
                  </Button>
                )}
              </div>
            </>
          )}
        </Card>

        {/* Credits card (D-010 packs) */}
        <Card className="p-5">
          <div className="flex items-baseline justify-between gap-2">
            <p className="type-label text-secondary">{t.credits.title}</p>
            <span className="type-caption text-muted">{t.credits.balanceLabel}</span>
          </div>
          {creditsLoading ? (
            <div className="mt-3">
              <Skeleton className="h-8 w-1/3" />
            </div>
          ) : creditsError ? (
            <p className="type-body-sm text-danger mt-3" role="alert">
              {creditsError}
            </p>
          ) : (
            <>
              <p className="type-price text-accent mt-2 tnum" style={{ fontSize: '28px', lineHeight: '32px' }}>
                {t.credits.balance(creditsData?.balance ?? 0)}
              </p>
              <p className="type-label text-secondary mt-5 mb-2">{t.credits.packsLabel}</p>
              <div className="flex flex-col gap-2 sm:flex-row">
                {packs.map((pack) => (
                  <Button
                    key={pack.pack}
                    variant="secondary"
                    size="md"
                    className="flex-1"
                    loading={buyingPack === pack.pack}
                    disabled={buyingPack !== null}
                    onClick={() => buyPack(pack)}
                  >
                    {buyingPack === pack.pack ? t.credits.opening : t.credits.packCta(pack.credits, fmt.money(pack.price_cents, pack.currency))}
                  </Button>
                ))}
              </div>
            </>
          )}
        </Card>

        <Card className="p-5">
          <p className="type-label text-secondary mb-1">{t.account.session}</p>
          <p className="type-body text-primary">{user?.email ? t.account.signedInAs(user.email) : '—'}</p>
          <div className="mt-4">
            <Button
              variant="destructive"
              size="md"
              onClick={async () => {
                await signOut();
                navigate('/');
              }}
            >
              {t.common.signOut}
            </Button>
          </div>
        </Card>

        <Card className="p-5">
          <p className="type-label text-secondary mb-3">{t.account.language}</p>
          <div className="flex gap-2">
            {(['es-MX', 'en-US'] as const).map((l) => (
              <Button key={l} variant={locale === l ? 'primary' : 'secondary'} size="md" onClick={() => setLocale(l)} lang={l}>
                {l === 'es-MX' ? 'Español' : 'English'}
              </Button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
